<template>
  <div class="">
    <div v-show="tooMuchData" class="card-panel red lighten-4">
      <div class="text-block">
        <h6>Too much data</h6>
          <b>{{ errorMessage }}</b>
      </div>
    </div>
    <HighChart v-show="!tooMuchData" :options="chartOptions" ref="chart"/>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'
import Highcharts from 'highcharts'
import moment from 'moment'

export default {
  name: 'AnalysisGraph',
  components: {
    HighChart: Chart
  },
  props: {
    railObject: {
      required: true
    },
    apiData: {
      required: true
    },
    xAxis: {
      required: true
    },
    yAxis: {
      required: true
    },
    xAxisTranslated: {
      required: true
    },
    yAxisTranslated: {
      required: true
    },
    unit: {
      required: true
    }
  },
  data () {
    return {
      tooMuchData: false,
      errorMessage: '',
      chartOptions: {
        chart: {
          zoomType: 'xy',
          zooming: {
            mouseWheel: {
              enabled: true
            }
          },
          events: {
            load () {
              const chart = this

              // Block scroll wheel unless Shift is held
              chart._wheelBlocker = (e) => {
                if (!e.shiftKey) {
                  if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                  e.stopPropagation()
                }
              }
              chart.container.addEventListener('wheel', chart._wheelBlocker, { capture: true })

              // Show zoom cursor when Shift is held
              chart._cursorHint = (e) => {
                chart.container.style.cursor = e.shiftKey ? 'zoom-in' : ''
              }
              chart.container.addEventListener('mousemove', chart._cursorHint)

              // Flip cursor immediately on Shift press/release
              chart._onKeyShift = (e) => {
                if (e.key === 'Shift' && chart.container) {
                  chart.container.style.cursor = (e.type === 'keydown') ? 'zoom-in' : ''
                }
              }
              chart._offKeyDown = Highcharts.addEvent(document, 'keydown', chart._onKeyShift)
              chart._offKeyUp = Highcharts.addEvent(document, 'keyup', chart._onKeyShift)
            },
            destroy () {
              const el = this.container
              if (this._wheelBlocker && el) el.removeEventListener('wheel', this._wheelBlocker, { capture: true })
              if (this._cursorHint && el) el.removeEventListener('mousemove', this._cursorHint)
              if (this._offKeyDown) this._offKeyDown()
              if (this._offKeyUp) this._offKeyUp()
            }
          }
        },
        title: {
          text: 'Object Name'
        },
        credits: {
          enabled: false
        },
        subtitle: {
          text: 'yAxis vs xAxis'
        },
        accessibility: {
          enabled: false
        },
        lang: {
          noData: this.$t('charts.nodata')
        },
        legend: {
          enabled: true
        },
        xAxis: {
          title: {
            text: 'xAxis'
          },
          type: 'linear'
        },
        yAxis: {
          title: {
            text: 'yAxis'
          }
        },
        plotOptions: {
          scatter: {
            marker: {
              radius: 5,
              states: {
                hover: {
                  enabled: false
                }
              }
            },
            states: {
              hover: {
                marker: {
                  enabled: false
                }
              }
            }
          }
        },
        series: [
          {
            name: this.$t('charts.normal'),
            type: 'scatter',
            data: [],
            turboThreshold: 10000
          },
          {
            name: this.$t('charts.reverse'),
            type: 'scatter',
            data: [],
            turboThreshold: 10000
          }
        ]
      },
      formattedData: []
    }
  },
  methods: {
    formatDateTime: (dateTime) => {
      return moment(String(dateTime)).format('DD-MM-YYYY')
    },
    formatChartData () {
      if (this.apiData !== null) {
        const xx = this.xAxis
        const yy = this.yAxis
        let formattedData = [[], []]

        if (this.apiData.length < 10000) {
          this.tooMuchData = false

          let minValue
          let maxValue
          if (this.apiData.length > 4 && yy !== 'Alarm') {
            const values = this.apiData.map(v => v[yy]).sort((a, b) => a - b)
            const q1 = this.getQuantile(values, 25)
            const q3 = this.getQuantile(values, 75)

            const iqr = q3 - q1
            maxValue = q3 + iqr * 1.5
            minValue = q1 - iqr * 1.5
          }

          this.apiData.forEach(function (m) {
            if (minValue && m[yy] < minValue) {
              formattedData[m.Direction].push({
                x: xx === 'Date' ? Date.parse(m.MeasurementDateTime) : m[xx],
                y: minValue,
                originalValue: m[yy],
                marker: {
                  lineWidth: 2,
                  lineColor: '#FF0000'
                }
              })
            } else if (maxValue && m[yy] > maxValue) {
              formattedData[m.Direction].push({
                x: xx === 'Date' ? Date.parse(m.MeasurementDateTime) : m[xx],
                y: maxValue,
                originalValue: m[yy],
                marker: {
                  lineWidth: 2,
                  lineColor: '#FF0000'
                }
              })
            } else {
              formattedData[m.Direction].push({
                x: xx === 'Date' ? Date.parse(m.MeasurementDateTime) : m[xx],
                y: m[yy]
              })
            }
          })
          this.formattedData = formattedData
        } else {
          this.tooMuchData = true
          var correctStartDateForSelection = 'unknown'
          var correctEndDateForSelection = 'unknown'

          // Get last 10k API datapoints
          var apiDataFirstTenK = this.apiData.slice(0, 9999)
          var apiDataLastTenK = this.apiData.slice(Math.max(this.apiData.length - 9999, 1))

          // Calculate minimum date
          if (apiDataLastTenK && apiDataLastTenK !== null && apiDataLastTenK !== undefined && apiDataLastTenK.length > 0) {
            // Get lowest date in dataset
            var apiDataLowestDate = apiDataLastTenK[0].Date
            // Check if lowerst date and up is exceeding 10K records
            var correctLowApiData = this.apiData.filter(ad => ad.Date >= apiDataLowestDate)

            while (correctLowApiData.length > 9999) {
              // set date + 1 and check to see if total array length is under 10k
              apiDataLowestDate++
              correctLowApiData = this.apiData.filter(ad => ad.Date >= apiDataLowestDate)
            }
            correctStartDateForSelection = correctLowApiData[0].Date
          }

          // Calculate maximum date in selection
          if (apiDataFirstTenK && apiDataFirstTenK !== null && apiDataFirstTenK !== undefined && apiDataFirstTenK.length > 0) {
            // Get highest date in dataset
            var apiDataHighestDate = apiDataFirstTenK[apiDataFirstTenK.length - 1].Date
            // Check if dataset is exceeding 10K
            var correctHighApiData = this.apiData.filter(ad => ad.Date <= apiDataHighestDate)
            while (correctHighApiData.length > 9999) {
              apiDataHighestDate--
              correctHighApiData = this.apiData.filter(ad => ad.Date <= apiDataHighestDate)
            }
            correctEndDateForSelection = correctHighApiData[correctHighApiData.length - 1].Date
            correctEndDateForSelection++
          }
          this.errorMessage =
            '\nThere is too much data to plot, please decrease your date selection range.\n\nSet your start date to: ' +
            this.formatDateTime(correctStartDateForSelection) +
            '\nor\nSet your end date to: ' +
            this.formatDateTime(correctEndDateForSelection)
        }
        this.$refs.chart.chart.redraw()
      }
    },
    setChartData () {
      const alarmLabels = ['None', 'Normal', 'Warning', 'Alarm']
      this.formatChartData()
      this.chartOptions.series[0].data = this.formattedData[0]
      this.chartOptions.series[1].data = this.formattedData[1]
      if (this.xAxis === 'Date') {
        this.chartOptions.xAxis = {
          type: 'datetime',
          min: null,
          max: null,
          title: {
            text: this.$t('views.alarmlist.date')
          },
          labels: {
            formatter: function () {
              return Highcharts.dateFormat('%e %b %Y', this.value)
            }
          }
        }
        this.chartOptions.tooltip = {
          formatter: function () {
            let tooltip = Highcharts.dateFormat('%e %b %Y %H:%M', new Date(this.x)) + '<br/>Value: '
            tooltip += (this.point.originalValue) ? '<b>' + this.point.originalValue + '</b>' : '<b>' + this.y + '</b>'
            return tooltip
          }
        }
      } else {
        this.chartOptions.xAxis = {
          type: 'linear',
          min: null,
          max: null,
          labels: {
            formatter () {
              return this.value
            }
          },
          title: {
            text: this.xAxis
          }
        }
        this.chartOptions.tooltip = {
          formatter: function () {
            let tooltip = this.x + '<br/>Value: '
            tooltip += (this.point.originalValue) ? '<b>' + this.point.originalValue + '</b>' : '<b>' + this.y + '</b>'
            return tooltip
          }
        }
      }
      if (this.yAxis === 'Alarm') {
        this.chartOptions.yAxis.categories = alarmLabels
        this.chartOptions.yAxis.reversed = true
      } else {
        this.chartOptions.yAxis.categories = null
        this.chartOptions.yAxis.reversed = false
      }
      this.chartOptions.yAxis.title.text = this.yAxisTranslated + ' ' + this.unit
      this.chartOptions.title.text = this.railObject.ComplexName + ' ' + this.railObject.Name
      this.chartOptions.subtitle.text = this.yAxisTranslated + ' vs ' + this.xAxisTranslated
    },
    getQuantile (values, quantile) {
      // Get the index the quantile is at.
      let index = quantile / 100.0 * (values.length - 1)

      // Check if it has decimal places.
      if (index % 1 === 0) {
        return values[index]
      } else {
        // Get the lower index.
        let lowerIndex = Math.floor(index)
        // Get the remaining.
        let remainder = index - lowerIndex
        // Add the remaining to the lowerindex value.
        return values[lowerIndex] + remainder * (values[lowerIndex + 1] - values[lowerIndex])
      }
    }
  },
  watch: {
    apiData (val) {
      if (val) {
        this.setChartData()
      }
    },
    xAxis (newValue) {
      if (this.apiData[0].hasOwnProperty(newValue)) {
        this.setChartData()
      }
    },
    yAxis (newValue) {
      if (this.apiData[0].hasOwnProperty(newValue) && newValue !== 'Date') {
        this.setChartData()
      }
    }
  }
}
</script>

<style scoped>
  .title {
    font-family: Roboto;
    font-style: normal;
    font-weight: 500;
    font-size: 20px;
    line-height: 23px !important;
    color: rgba(0, 0, 0, 0.87);
  }

  .text-block {
    white-space: pre;
  }
</style>



// WEBPACK FOOTER //
// src/components/switch/analysis/XYAnalysisGraph.vue